import { Controller } from '@hotwired/stimulus';
import consumer from '../../../../javascript/channels/consumer'

declare const Handsontable: any;

declare const NProgress: {
  start: () => void;
  done: () => void;
};

export default class extends Controller {
  static targets = ["errorMessages", "errorMessagesSingle", "fileInput", "removeButton"];

  declare readonly errorMessagesTarget: HTMLElement;
  declare readonly errorMessagesSingleTarget: HTMLElement;
  declare readonly hasErrorMessagesSingleTarget: boolean;
  declare readonly fileInputTarget: HTMLInputElement;
  declare readonly removeButtonTarget: HTMLButtonElement;

  subscription: any = null;
  loading_subscription: any = null;
  uploadId: any = null;
  currentUploadMethod: string = 'handsontable';

  turboFrameLoadListener: (event: Event) => void = () => {};

  connect() {
    const controller = this;

    this.handleHandsontableData = this.handleHandsontableData.bind(this);

    this.element.addEventListener("handsontable:data", this.handleHandsontableData);

    this.turboFrameLoadListener = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.id === "hot-frame") {
        this.injectDropdownWhenReady();
      }
    };

    document.addEventListener("turbo:frame-load", this.turboFrameLoadListener);

    this.subscription = consumer.subscriptions.create( 
      { channel: 'UploadChannel' },
      {
        connected() {
        },

        disconnected() {
        },

        received(data: { status: string; errors: any; upload_id: any; }) {
          if (data.status === 'error') {
            if (!controller.errorMessagesTarget || !data.errors) return;
              const errorMessage = controller.currentUploadMethod === 'csv'
                ? 'Please fix the errors in the csv, download the error report for details.'
                : 'Please fix the errors in the table, download the error report for details.';
              controller.showErrorMessages(errorMessage);
              controller.uploadId = data.upload_id;
              controller.showDownloadErrorOptions();

              if (controller.currentUploadMethod === 'handsontable') {
                const container = document.querySelector(".handsontable-wrapper");
                if (container) {
                  const errorEvent = new CustomEvent("handsontable:errors", {
                    detail: { errors: data.errors },
                    bubbles: true
                  });
                  container.dispatchEvent(errorEvent);
                }
              }
          } else if (data.status === 'success') {
            controller.showSuccessMessage("Upload Successful!");
          }
        }
      }
    );

    this.loading_subscription = consumer.subscriptions.create(
      { channel: 'UploadLoadingIndicatorChannel' },
      {
        connected() {
        },

        disconnected() {
        },

        received(data: { status: "start" | "done" }) {
          if (data.status === "start") {
            NProgress.start();
          } else {
            NProgress.done();
          }
        },
      }
    );
  }


  disconnect() {
    this.element.removeEventListener("handsontable:data", this.handleHandsontableData);
    document.removeEventListener("turbo:frame-load", this.turboFrameLoadListener);

    if (this.subscription) {
      console.log("Unsubscribing from CsvUploadChannel");
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    if (this.loading_subscription) {
      console.log("Unsubscribing from ApiDashboardLoadingIndicatorChannel");
      this.loading_subscription.unsubscribe();
      this.loading_subscription = null;
    }
  }

  triggerGather() {
    document.dispatchEvent(new Event("gatherHandsontableData"));
  }

  handleHandsontableData(event: any) {
    const payloadField = this.element.querySelector<HTMLInputElement>('#handsontable-payload');

    if (payloadField) {
      payloadField.value = JSON.stringify(event.detail)
    }
  }

submitForm(event: SubmitEvent) {
  this.clearErrorMessages();

  if (this.currentUploadMethod === 'csv') {
    const fileInput = this.element.querySelector<HTMLInputElement>('input[type="file"][name="csv_file"]');
    if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showErrorMessages('No file uploaded. Please upload your file before validation.');
      return;
    }
  }

  document.dispatchEvent(new Event("gatherHandsontableData"));

  const form = event.target as HTMLFormElement;
}

  submitSingleForm(event: MouseEvent) {
    event.preventDefault();
        
    if (this.hasErrorMessagesSingleTarget) {
      this.clearSingleErrorMessages();
    }

    const button = event.currentTarget as HTMLButtonElement;
    const form = button.closest('form') as HTMLFormElement;
    if (!form) return;

    const url = new URL(form.action);
    const formData = new FormData(form);
    
    formData.forEach((value, key) => {
      url.searchParams.set(key, value as string);
    });

    fetch(url.toString())
      .then(async response => {
        if (!response.ok) {
          const data: { error?: string } = await response.json();
          this.showSingleErrorMessage(data.error || 'An error occurred');
          return;
        }
        window.location.href = url.toString();
      })
      .catch(() => {
        this.showSingleErrorMessage('An unexpected error occurred');
      });
  }

  clearSingleErrorMessages() {
    if (this.hasErrorMessagesSingleTarget) {
      this.errorMessagesSingleTarget.innerHTML = '';
    }
  }

  showSingleErrorMessage(message: string) {
    if (this.hasErrorMessagesSingleTarget) {
      this.errorMessagesSingleTarget.classList.remove('success-messages');
      this.errorMessagesSingleTarget.classList.add('error-messages');
      this.errorMessagesSingleTarget.innerHTML = message;
    }
  }

  handleSuccess(event: any) {
    const form = event.target as HTMLFormElement;

    const isHandsontable = form.classList.contains("handsontable-form");
    const isCsvUpload = form.classList.contains("csv-upload-form");

    if (!isHandsontable && !isCsvUpload) return;

    this.errorMessagesTarget.innerText = '';
  }

  handleError(event: any) {
    const form = event.target as HTMLFormElement;
    const isHandsontable = form.classList.contains("handsontable-form");
    const isCsvUpload = form.classList.contains("csv-upload-form");

    if (!isHandsontable && !isCsvUpload) return;

    const [data] = event.detail;

    const errors = data?.errors ?? [];

    if (isHandsontable) {
      const container = document.querySelector(".handsontable-wrapper");
      if (container) {
        const errorEvent = new CustomEvent("handsontable:errors", {
          detail: { errors },
          bubbles: true
        });
        container.dispatchEvent(errorEvent);
      }
    }

    const errorMessage = this.currentUploadMethod === 'csv'
      ? 'Please fix the errors in the csv, download the error report for details.'
      : 'Please fix the errors in the table, download the error report for details.';
    this.showErrorMessages(errorMessage);

    if (data?.upload_id) {
      this.uploadId = data.upload_id;
      this.showDownloadErrorOptions();
    }
  }

  clearErrorMessages() {
    if (this.errorMessagesTarget) {
      this.errorMessagesTarget.innerHTML = "";
    }
  }

  showErrorMessages(message: string) {
    if (this.errorMessagesTarget) {
      this.errorMessagesTarget.classList.remove('success-messages');
      this.errorMessagesTarget.classList.add('error-messages');
      this.errorMessagesTarget.innerHTML = message;
    }
  }

  showSuccessMessage(message: string) {
    if (this.errorMessagesTarget) {
      this.errorMessagesTarget.classList.remove('error-messages');
      this.errorMessagesTarget.classList.add('success-messages');
      this.errorMessagesTarget.innerHTML = message;
    }
  }

  injectDropdownWhenReady() {
    const handsontableButtonSet = this.element.querySelector("[data-handsontable--handsontable-component--handsontable-component-target=handsontableButtons]");

    if (handsontableButtonSet instanceof HTMLElement) {
      this.addButtons(handsontableButtonSet);
    }
  }

  addButtons(handsontableButtonSet: HTMLElement) {
    const dropdown = document.getElementById("dashboard-dropdown");
    if (!dropdown) return;

    const handsontableForm = document.querySelector(".handsontable-submit-container form");
    if (!handsontableForm) return;

    handsontableButtonSet.prepend(handsontableForm);
    handsontableButtonSet.appendChild(dropdown);

    dropdown.classList.remove('hidden');
  }

  toggleUploadMethod(event: Event) {
    this.clearErrorMessages();
    this.hideDownloadErrorOptions();

    const target = event.target as HTMLInputElement;

    if (!target) return;

    this.currentUploadMethod = target.value === 'csv' ? 'csv' : 'handsontable';

    const handsontableSection = document.getElementById("handsontable-section");;
    const csvUploadSection = document.getElementById("csv-upload-section");

    if (!handsontableSection || !csvUploadSection) return;

    if (target.value === "handsontable") {
      handsontableSection.classList.remove("hidden");
      csvUploadSection.classList.add("hidden");
    } else if (target.value === "csv") {
      handsontableSection.classList.add("hidden");
      csvUploadSection.classList.remove("hidden");
    }
  }

  clearFileInput() {
    const fileInput = this.element.querySelector<HTMLInputElement>('input[type="file"][name="csv_file"]');
    if (fileInput) {
      fileInput.value = "";
      this.removeButtonTarget.disabled = true;
    }

    this.clearErrorMessages();
  }

  fileSelected() {
    if (this.fileInputTarget.files && this.fileInputTarget.files.length > 0) {
      this.removeButtonTarget.disabled = false;
    } else {
      this.removeButtonTarget.disabled = true;
    }

    this.clearErrorMessages();
  }

  showDownloadErrorOptions() {
    const csvLinks = this.element.querySelectorAll<HTMLAnchorElement>('.download-error-csv');
    csvLinks.forEach(link => link.classList.remove('hidden'));

    const xlsxLinks = this.element.querySelectorAll<HTMLAnchorElement>('.download-error-xlsx');
    xlsxLinks.forEach(link => link.classList.remove('hidden'));
  }

  hideDownloadErrorOptions() {
    const csvLinks = this.element.querySelectorAll<HTMLAnchorElement>('.download-error-csv');
    csvLinks.forEach(link => link.classList.add('hidden'));

    const xlsxLinks = this.element.querySelectorAll<HTMLAnchorElement>('.download-error-xlsx');
    xlsxLinks.forEach(link => link.classList.add('hidden'));

    this.uploadId = null;
  }

  downloadErrorCsv(event: Event) {
    event.preventDefault();

    if (!this.uploadId) {
      console.error("No upload ID available for error CSV download.");
      return;
    }

    const url = `/admin/api_dashboard_download/errors?upload_id=${this.uploadId}&report_format=csv`;

    window.location.href = url;
  }

  downloadErrorXlsx() {
    if (!this.uploadId) {
      console.error("No upload ID available for error XLSX download.");
      return;
    }

    const url = `/admin/api_dashboard_download/errors?upload_id=${this.uploadId}&report_format=xlsx`;

    window.location.href = url;
  }
}